"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.processHeadingMutations = processHeadingMutations;
var _rdflib = require("rdflib");
var _solidUi = require("solid-ui");
var _rdfMutationHelpers = require("../shared/rdfMutationHelpers");
var _idNodeFactory = require("../shared/idNodeFactory");
var _texts = require("../../texts");
var _debug = require("../../utils/debug");
function splitPronouns(value) {
  const normalized = (value || '').trim();
  if (!normalized) return {};
  const [subjectPronounRaw, objectPronounRaw] = normalized.split('/').map(part => part.trim());
  const subjectPronoun = subjectPronounRaw || undefined;
  const objectPronoun = objectPronounRaw || undefined;
  return {
    subjectPronoun,
    objectPronoun
  };
}
function buildPhoneStatements(subject, doc, node, phone) {
  const normalizedValue = phone.value.startsWith('tel:') ? phone.value : `tel:${phone.value}`;
  const valueNode = (0, _rdflib.sym)(normalizedValue);
  const inserts = [(0, _rdflib.st)(subject, _solidUi.ns.vcard('hasTelephone'), node, doc), (0, _rdflib.st)(node, _solidUi.ns.vcard('value'), valueNode, doc)];
  if (phone.type) {
    inserts.push((0, _rdflib.st)(node, _solidUi.ns.rdf('type'), _solidUi.ns.vcard(phone.type), doc));
  }
  return inserts;
}
function buildEmailStatements(subject, doc, node, email) {
  const normalizedValue = email.value.startsWith('mailto:') ? email.value : `mailto:${email.value}`;
  const valueNode = (0, _rdflib.sym)(normalizedValue);
  const inserts = [(0, _rdflib.st)(subject, _solidUi.ns.vcard('hasEmail'), node, doc), (0, _rdflib.st)(node, _solidUi.ns.vcard('value'), valueNode, doc)];
  if (email.type) {
    inserts.push((0, _rdflib.st)(node, _solidUi.ns.rdf('type'), _solidUi.ns.vcard(email.type), doc));
  }
  return inserts;
}
function buildAddressStatements(subject, doc, node, address) {
  const inserts = [(0, _rdflib.st)(subject, _solidUi.ns.vcard('hasAddress'), node, doc)];
  if (address.type) inserts.push((0, _rdflib.st)(node, _solidUi.ns.rdf('type'), _solidUi.ns.vcard(address.type), doc));
  if (address.streetAddress) inserts.push((0, _rdflib.st)(node, _solidUi.ns.vcard('street-address'), (0, _rdflib.literal)(address.streetAddress), doc));
  if (address.locality) inserts.push((0, _rdflib.st)(node, _solidUi.ns.vcard('locality'), (0, _rdflib.literal)(address.locality), doc));
  if (address.region) inserts.push((0, _rdflib.st)(node, _solidUi.ns.vcard('region'), (0, _rdflib.literal)(address.region), doc));
  if (address.postalCode) inserts.push((0, _rdflib.st)(node, _solidUi.ns.vcard('postal-code'), (0, _rdflib.literal)(address.postalCode), doc));
  if (address.countryName) inserts.push((0, _rdflib.st)(node, _solidUi.ns.vcard('country-name'), (0, _rdflib.literal)(address.countryName), doc));
  return inserts;
}
function findOpWithExistingEntry(ops, existingNodes) {
  return ops.find(op => Boolean(op.entryNode) && Boolean((0, _rdfMutationHelpers.findExistingNode)(existingNodes, op.entryNode)));
}
function findCreateOp(ops) {
  return ops.find(op => !op.entryNode);
}
async function mutatePhoneEntry(store, subject, phoneOps, documentTextCache) {
  const doc = subject.doc();
  const existingPhoneNodes = store.each(subject, _solidUi.ns.vcard('hasTelephone'), null, doc);
  const deletions = [];
  const insertions = [];
  const removePhone = findOpWithExistingEntry(phoneOps.remove, existingPhoneNodes);
  const updatePhone = findOpWithExistingEntry(phoneOps.update, existingPhoneNodes);
  const createPhone = findCreateOp(phoneOps.create) || phoneOps.create[0];
  if (removePhone?.entryNode) {
    const existingNode = (0, _rdfMutationHelpers.findExistingNode)(existingPhoneNodes, removePhone.entryNode);
    if (existingNode) {
      const linkedPhoneStatements = (0, _rdfMutationHelpers.collectLinkedNodeStatements)(store, subject, _solidUi.ns.vcard('hasTelephone'), doc);
      const matchingLinkStatement = linkedPhoneStatements.linkStatements.find(statement => statement.object?.value === existingNode.value);
      if (matchingLinkStatement) {
        deletions.push(matchingLinkStatement);
      }
      deletions.push(...(0, _rdfMutationHelpers.collectNodeStatements)(store, existingNode, doc));
    }
  }
  if (updatePhone) {
    const existingNode = (0, _rdfMutationHelpers.findExistingNode)(existingPhoneNodes, updatePhone.entryNode);
    if (existingNode) {
      deletions.push(...(0, _rdfMutationHelpers.collectNodeStatements)(store, existingNode, doc));
      insertions.push(...buildPhoneStatements(subject, doc, existingNode, updatePhone));
    }
  }
  if (createPhone) {
    insertions.push(...buildPhoneStatements(subject, doc, (0, _idNodeFactory.createIdNode)(doc), createPhone));
  }
  const shouldSerializeDocument = await (0, _rdfMutationHelpers.shouldForceDocumentPutForStatements)(store, doc, insertions, undefined, {
    documentTextCache
  });
  await (0, _rdfMutationHelpers.runUpdateTransport)(store, doc, deletions, insertions, {
    unsupportedMessage: _texts.updaterUnsupportedStoreErrorMessageText,
    failureMessage: 'Failed to save heading updates',
    documentTextCache,
    useDavFallback: false,
    usePutFallback: shouldSerializeDocument,
    forcePut: shouldSerializeDocument
  });
}
async function mutateEmailEntry(store, subject, emailOps, documentTextCache) {
  const doc = subject.doc();
  const existingEmailNodes = store.each(subject, _solidUi.ns.vcard('hasEmail'), null, doc);
  const deletions = [];
  const insertions = [];
  const removeEmail = findOpWithExistingEntry(emailOps.remove, existingEmailNodes);
  const updateEmail = findOpWithExistingEntry(emailOps.update, existingEmailNodes);
  const createEmail = findCreateOp(emailOps.create) || emailOps.create[0];
  if (removeEmail?.entryNode) {
    const existingNode = (0, _rdfMutationHelpers.findExistingNode)(existingEmailNodes, removeEmail.entryNode);
    if (existingNode) {
      const linkedEmailStatements = (0, _rdfMutationHelpers.collectLinkedNodeStatements)(store, subject, _solidUi.ns.vcard('hasEmail'), doc);
      const matchingLinkStatement = linkedEmailStatements.linkStatements.find(statement => statement.object?.value === existingNode.value);
      if (matchingLinkStatement) {
        deletions.push(matchingLinkStatement);
      }
      deletions.push(...(0, _rdfMutationHelpers.collectNodeStatements)(store, existingNode, doc));
    }
  }
  if (updateEmail) {
    const existingNode = (0, _rdfMutationHelpers.findExistingNode)(existingEmailNodes, updateEmail.entryNode);
    if (existingNode) {
      deletions.push(...(0, _rdfMutationHelpers.collectNodeStatements)(store, existingNode, doc));
      insertions.push(...buildEmailStatements(subject, doc, existingNode, updateEmail));
    }
  }
  if (createEmail) {
    insertions.push(...buildEmailStatements(subject, doc, (0, _idNodeFactory.createIdNode)(doc), createEmail));
  }
  const shouldSerializeDocument = await (0, _rdfMutationHelpers.shouldForceDocumentPutForStatements)(store, doc, insertions, undefined, {
    documentTextCache
  });
  await (0, _rdfMutationHelpers.runUpdateTransport)(store, doc, deletions, insertions, {
    unsupportedMessage: _texts.updaterUnsupportedStoreErrorMessageText,
    failureMessage: 'Failed to save heading updates',
    documentTextCache,
    useDavFallback: false,
    usePutFallback: shouldSerializeDocument,
    forcePut: shouldSerializeDocument
  });
}
async function mutateAddressEntry(store, subject, addressOps, documentTextCache) {
  const doc = subject.doc();
  const existingAddressNodes = store.each(subject, _solidUi.ns.vcard('hasAddress'), null, doc);
  const deletions = [];
  const insertions = [];
  const removeAddress = findOpWithExistingEntry(addressOps.remove, existingAddressNodes);
  const updateAddress = findOpWithExistingEntry(addressOps.update, existingAddressNodes);
  const createAddress = findCreateOp(addressOps.create) || addressOps.create[0];
  if (removeAddress?.entryNode) {
    const existingNode = (0, _rdfMutationHelpers.findExistingNode)(existingAddressNodes, removeAddress.entryNode);
    if (existingNode) {
      const linkedAddressStatements = (0, _rdfMutationHelpers.collectLinkedNodeStatements)(store, subject, _solidUi.ns.vcard('hasAddress'), doc);
      const matchingLinkStatement = linkedAddressStatements.linkStatements.find(statement => statement.object?.value === existingNode.value);
      if (matchingLinkStatement) {
        deletions.push(matchingLinkStatement);
      }
      deletions.push(...(0, _rdfMutationHelpers.collectNodeStatements)(store, existingNode, doc));
    }
  }
  if (updateAddress) {
    const existingNode = (0, _rdfMutationHelpers.findExistingNode)(existingAddressNodes, updateAddress.entryNode);
    if (existingNode) {
      deletions.push(...(0, _rdfMutationHelpers.collectNodeStatements)(store, existingNode, doc));
      insertions.push(...buildAddressStatements(subject, doc, existingNode, updateAddress));
    }
  }
  if (createAddress) {
    insertions.push(...buildAddressStatements(subject, doc, (0, _idNodeFactory.createIdNode)(doc), createAddress));
  }
  const shouldSerializeDocument = await (0, _rdfMutationHelpers.shouldForceDocumentPutForStatements)(store, doc, insertions, undefined, {
    documentTextCache
  });
  await (0, _rdfMutationHelpers.runUpdateTransport)(store, doc, deletions, insertions, {
    unsupportedMessage: _texts.updaterUnsupportedStoreErrorMessageText,
    failureMessage: 'Failed to save heading updates',
    documentTextCache,
    useDavFallback: false,
    usePutFallback: shouldSerializeDocument,
    forcePut: shouldSerializeDocument
  });
}
async function mutateBasicProfileEntry(store, subject, basicOps, documentTextCache) {
  const doc = subject.doc();
  const deletions = [];
  const insertions = [];
  const replaceLiteralField = (predicate, value) => {
    const normalized = (value || '').trim();
    const nextObject = normalized ? (0, _rdflib.literal)(normalized) : null;
    (0, _rdfMutationHelpers.replacePredicateStatements)(store, subject, predicate, doc, deletions, insertions, nextObject);
  };
  const replacePhotoField = value => {
    const normalized = (value || '').trim();
    const nextObject = !normalized ? null : normalized.startsWith('http://') || normalized.startsWith('https://') ? (0, _rdflib.sym)(normalized) : (0, _rdflib.literal)(normalized);
    (0, _rdfMutationHelpers.replacePredicateStatements)(store, subject, _solidUi.ns.vcard('hasPhoto'), doc, deletions, insertions, nextObject);
  };
  const replacePronounFields = value => {
    const {
      subjectPronoun,
      objectPronoun
    } = splitPronouns(value);
    (0, _rdfMutationHelpers.replacePredicateStatements)(store, subject, _solidUi.ns.solid('preferredSubjectPronoun'), doc, deletions, insertions, subjectPronoun ? (0, _rdflib.literal)(subjectPronoun) : null);
    (0, _rdfMutationHelpers.replacePredicateStatements)(store, subject, _solidUi.ns.solid('preferredObjectPronoun'), doc, deletions, insertions, objectPronoun ? (0, _rdflib.literal)(objectPronoun) : null);
    (0, _rdfMutationHelpers.replacePredicateStatements)(store, subject, _solidUi.ns.solid('preferredRelativePronoun'), doc, deletions, insertions, null);
  };
  const applyBasics = (basic, clearAll = false) => {
    const data = clearAll ? {
      name: '',
      nickname: '',
      pronouns: '',
      dateOfBirth: '',
      imageSrc: ''
    } : basic;
    replaceLiteralField(_solidUi.ns.vcard('fn'), data.name);
    // Keep foaf:nick in sync where available in existing data.
    replaceLiteralField(_solidUi.ns.foaf('nick'), data.nickname);
    replaceLiteralField(_solidUi.ns.vcard('nickname'), data.nickname);
    replaceLiteralField(_solidUi.ns.vcard('bday'), data.dateOfBirth);
    replacePronounFields(data.pronouns);
    replacePhotoField(data.imageSrc);
  };
  const removeBasic = basicOps.remove[0];
  const updateBasic = basicOps.update[0];
  const createBasic = basicOps.create[0];
  const selectedBasic = updateBasic || createBasic || removeBasic;
  if (selectedBasic) {
    applyBasics(selectedBasic, Boolean(removeBasic && !updateBasic && !createBasic));
  }
  const shouldSerializeDocument = await (0, _rdfMutationHelpers.shouldForceDocumentPutForStatements)(store, doc, insertions, undefined, {
    documentTextCache
  });
  await (0, _rdfMutationHelpers.runUpdateTransport)(store, doc, deletions, insertions, {
    unsupportedMessage: _texts.updaterUnsupportedStoreErrorMessageText,
    failureMessage: 'Failed to save heading updates',
    documentTextCache,
    useDavFallback: false,
    usePutFallback: shouldSerializeDocument,
    forcePut: shouldSerializeDocument
  });
}
async function processHeadingMutations(store, subject, mutationPlan) {
  try {
    const documentTextCache = new Map();
    await mutateBasicProfileEntry(store, subject, mutationPlan.basicOps, documentTextCache);
    await mutatePhoneEntry(store, subject, mutationPlan.phoneOps, documentTextCache);
    await mutateEmailEntry(store, subject, mutationPlan.emailOps, documentTextCache);
    await mutateAddressEntry(store, subject, mutationPlan.addressOps, documentTextCache);
  } catch (error) {
    const rootError = error instanceof Error ? error : new Error(String(error));
    (0, _debug.error)(_texts.headingMutationSaveFailedDebugText, rootError);
    throw new Error(_texts.saveHeadingUpdatesFailedMessageText, {
      cause: rootError
    });
  }
}